<template>
  <div
    class="collection-view">
    <!-- HEADER -->
    <div class="collection-header">
      <div class="collection-header-filler" />
      <div
        class="collection-header-search">
        <SearchBar />
      </div>
      <div class="collection-header-filler" />
      <div
        class="collection-header-tab"
        :class="{
          'collection-header-tab-inactive': !fileData.length,
          'collection-header-tab-viewer-added-indicator': displayAddedItemInBackgroundIndicator,
        }"
        @click="goToWebViewer">
        Viewer
      </div>
    </div>

    <!-- CONTENT CONTAINER -->
    <div
      class="collection-content">
      <!-- OPEN ITEMS -->
      <div class="open-item-tabs-scrollbox">
        <div class="open-item-tabs">
          <div
            class="open-item-tab open-item-collection-tab"
            :class="{
              'open-item-tab-active': $route.name === 'Collection',
            }"
            @click="$goToCollection({ id: selectedCollection })">
            <i class="mdi mdi-format-list-bulleted" />
            {{ selectedCollectionTitle }}
          </div>
          <div
            v-for="(item, index) in openItems"
            :key="item.item.id"
            class="open-item-tab"
            :class="{
              'open-item-tab-active': (item.item.id === Number($route.params.cardId) && item.item.type === 'card') || (item.item.id === Number($route.params.noteId) && item.item.type === 'note'),
            }"
            @click="goToItem(item.item)">
            <div
              class="open-item-tab-text">
              {{ item.item.title }}
            </div>
            <i
              v-if="item.item.id !== 0"
              class="mdi mdi-pin icon-pin"
              :class="{
                'mdi-pin-outline': !item.pinned,
                'hide-pin-icon': !item.pinned,
              }"
              @click.stop="togglePinOpenItem(item.item)" />
            <i
              class="mdi mdi-close icon-close"
              @click.stop="closeItem({ item: item.item, index, pinned: item.pinned })" />
          </div>
          <div
            class="open-item-tabs-filler"
            :style="{ 'width': `calc(100% - (${openItems.length + 1.5} * 8rem))` }" />
        </div>
      </div>

      <!-- CONTENT -->
      <CollectionView
        v-show="showCollectionView"
        @add-card="addCard"
        @add-note="addNotes" />
      <CardView
        v-show="showCardView"
        class="card-details"
        @move-card="initiateMoveItems" />
      <NoteView
        v-show="showNoteView" />
    </div>

    <AddButtons
      @add-card="addCard"
      @add-notes="addNotes" />
  </div>
</template>

<script>
import axios from 'axios';
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import SearchBar from '@/components/AppView/Search/SearchBar.vue';
import CollectionView from '@/components/AppView/CollectionView/CollectionView.vue';
import CardView from '@/components/AppView/CollectionView/CardView.vue';
import NoteView from '@/components/AppView/CollectionView/NoteView.vue';
import AddButtons from '@/components/AppView/CollectionView/AddButtons.vue';
import ModalMove from '@/components/AppView/CollectionView/ModalMove.vue';

export default {
  name: 'CollectionContentContainer',
  components: {
    SearchBar,
    CollectionView,
    CardView,
    NoteView,
    AddButtons,
    ModalMove, // eslint-disable-line
  },
  data() {
    return {
      collectionId: Number(this.$route.params.collectionId) || null,
      showAddCard: false,
      draftCard: null,
    };
  },
  computed: {
    ...mapState({
      selectedCollection: state => state.collections.selectedCollection,
      fileData: state => state.files.webViewerFileData,
      openFile: state => state.files.openFile,
      userId: state => state.users.internalUserId,
      allSelectedItems: state => state.appState.selectedItems,
      displayAddedItemInBackgroundIndicator: state => state.appState.displayAddedItemInBackgroundIndicator,
      openItems: state => state.appState.openItems,
      selectedCollectionTitle: state => state.collections.collectionNodes[state.collections.selectedCollection].name,
    }),
    ...mapGetters({
      v_selectedItems: 'appState/getSelectedItems',
    }),
    showCollectionView() {
      return this.$route.name === 'Collection' || this.$route.name === 'KeywordList' || this.$route.name === 'StarList';
    },
    showCardView() {
      return this.$route.name === 'Card';
    },
    showNoteView() {
      return this.$route.name === 'Note';
    },
    selectedItems() {
      // When on keywordList, selectedItems need to be looked up manually since Vuex does not know about this
      if (this.$route.name === 'KeywordList') {
        const keywordlistEntry = this.allSelectedItems.find(entry => entry.id === 'keywordlist');
        if (keywordlistEntry) return keywordlistEntry.items;
        return [];
      }
      if (this.$route.name === 'StarList') {
        const starlistEntry = this.allSelectedItems.find(entry => entry.id === 'starlist');
        if (starlistEntry) return starlistEntry.items;
        return [];
      }

      return this.v_selectedItems.items;
    },
  },
  methods: {
    ...mapActions({
      addCollectionNotes: 'notes/addCollectionNotes',
      updateCollectionNotes: 'notes/updateCollectionNotes',
      deleteCollectionNotes: 'notes/deleteCollectionNotes',
      togglePinOpenItem: 'appState/togglePinOpenItem',
      unselectItemsFrom: 'appState/unselectItemsFrom',
      toggleSelectedItem: 'appState/toggleSelectedItem',
    }),
    ...mapMutations({
      CloseOpenItem: 'appState/CloseOpenItem',
    }),
    closeItem({ item, index, pinned }) {
      if (pinned) this.togglePinOpenItem(item);
      this.CloseOpenItem(item);

      // If we are on the page of the closed item, we need to navigate elsewhere
      let routeItemId;
      if (item.type === 'card') routeItemId = Number(this.$route.params.cardId);
      else if (item.type === 'note') routeItemId = Number(this.$route.params.noteId);
      const onPageOfClosedItem = item.id === routeItemId;

      if (onPageOfClosedItem) {
        if (this.openItems.length) {
          const itemToGoTo = this.openItems[Math.min(index, this.openItems.length - 1)].item;
          this.goToItem(itemToGoTo);
        }
        else this.$goToCollection({ id: this.selectedCollection });
      }
    },
    goToItem(item) {
      if (item.type === 'card') this.$goToCard(item);
      else if (item.type === 'note') this.$goToNote(item);
    },
    goToWebViewer() {
      if (!this.fileData.length) {
        this.$buefy.toast.open({
          message: 'No documents open',
          type: 'is-secondary-dark',
        });
        return;
      }

      let index = this.fileData.findIndex(entry => entry.cardId === this.openFile);
      if (index < 0) index = 0;

      const card = this.fileData[index].ref;
      this.$goToItem(card);
    },
    initiateMoveItems(item = {}) {
      const { id, type } = item;

      this.$buefy.modal.open({
        parent: this,
        component: ModalMove,
        hasModalCard: true,
        trapFocus: true,
        props: {
          id,
        },
        events: {
          move: (collectionId) => {
            if (type === 'card') this.moveCard(collectionId, id);
            else if (type === 'note') this.moveNote(collectionId, id);
            else this.moveSelectedItems(collectionId);
          },
        },
      });
    },
    moveSelectedItems(newCollectionId) {
      this.selectedItems.forEach((selectedItem) => {
        if (selectedItem.type === 'card') this.moveCard(newCollectionId, selectedItem.id, true);
        else if (selectedItem.type === 'note') this.moveNote(newCollectionId, selectedItem.id, true);
      });

      this.unselectItemsFrom(this.selectId);
      this.unselectItemsFrom(newCollectionId);
    },
    moveCard(newCollectionId, id, doNotDeselect) {
      // For batch move actions, deselect per item is not necessary
      if (!doNotDeselect) this.toggleSelectedItem({ id: this.selectId, itemId: id, itemType: 'card', forceRemove: true });

      this.$updateCard({
        id,
        data: {
          relatedCollectionId: newCollectionId,
        },
      });
    },
    moveNote(newCollectionId, id, doNotDeselect) {
      // For batch move actions, deselect per item is not necessary
      if (!doNotDeselect) this.toggleSelectedItem({ id: this.selectId, itemId: id, itemType: 'note', forceRemove: true });

      this.moveCollectionNotes({ noteId: id, newCollectionId, dateLastModified: this.$getCurrentUTC() });
    },
    addCard() {
      const now = this.$getCurrentUTC();
      this.draftCard = Object.assign({}, this.$store.state.cards.draftCard, {
        dateCreated: now,
        dateLastModified: now,
        relatedCollectionId: this.selectedCollection,
      });
      this.showAddCard = true;
      this.$goToCard(this.draftCard); // Goes to card/0
    },
    getNoteId(dateCreated) {
      return new Promise(async (resolve, reject) => {
        const { data } = await axios.post(`collectionnotes/${this.userId}/${this.selectedCollection}`, { dateCreated });
        const { newId } = data;
        resolve(newId);
      });
    },
    async addNotes() {
      const now = this.$getCurrentUTC();
      // Request a new note id, will await it later
      const getNoteId = this.$getQuerablePromise(this.getNoteId(now));

      this.$buefy.dialog.prompt({
        message: 'Enter a title for your note',
        inputAttrs: {
          type: 'text',
          placeholder: 'My notes',
        },
        confirmText: 'Save',
        canCancel: true,
        trapFocus: true,
        onConfirm: async (value) => {
          if (getNoteId.isPending()) {
            this.$buefy.toast.open({
              message: 'One moment please',
              type: 'is-black',
            });
          }

          // Wait until we have the new note id
          const noteId = await getNoteId;
          this.addCollectionNotes({
            collectionId: this.selectedCollection,
            noteId,
            dateCreated: now,
            section: 'notes',
            notes: '<p></p>',
          });
          // dateLastModified and title are only added with updating, not with adding. Probably fix later.
          // Btw, section is also only added with updating (on server I mean).
          this.updateCollectionNotes({
            collectionId: this.selectedCollection,
            noteId,
            title: value,
            section: 'notes',
            dateLastModified: now,
          });
          this.$goToNote({ id: noteId, relatedCollectionId: this.selectedCollection });
        },
        onCancel: async () => {
          const noteId = await getNoteId;
          this.addCollectionNotes({
            collectionId: this.selectedCollection,
            noteId,
            dateCreated: now,
          });
          this.deleteCollectionNotes({
            collectionId: this.selectedCollection,
            noteId,
          });
        },
      });
    },
  },
  activated() {
    // Executes when switching between notes and cards or when going from e.g. Home to a collectionView
    if (this.collectionId < 0) {
      // We have a temporary collectionId, still requesting a new one from the server. Once we have a new id, navigate to that page
      // to do: check whether this is still necessary, given that new collections are added from the collection management page. To make it easy for yourself, you could
      // make sure that the new collection only shows up (or is clickable in management overview) once we have a proper id.
      const unsubscribe = this.$store.subscribe((mutation) => {
        if (mutation.type === 'collections/ReplaceNewCollectionId') {
          const { newId, tempId } = mutation.payload;
          if (tempId === this.collectionId) this.$goToCollection({ id: newId });
          unsubscribe();
        }
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/colors';
@import '~styles/fonts';
@import '~styles/layouts';

// VIEW
.collection-view {
  position: relative;
  height: 100vh;
  max-height: 100vh; // 2.875rem for navigation and stuff on top
  overflow: hidden;
}

// HEADER
.collection-header {
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  z-index: 5;
  border-bottom: 1px solid $primary;
  font-size: 1.25rem;
  height: 2.875rem; // (font-size * 1.5 line-height) + tab padding
  padding: 0;
  vertical-align: middle;
  user-select: none;
}
.collection-header-tab {
  flex: 0 0 auto;
  width: 8rem;
  text-align: center;
  margin: 0 0 -1px 0;
  padding: 0.5rem 0.25rem;
  cursor: pointer;
  transition: background-color 0.15s;
}
.collection-header-tab-inactive {
  cursor: default;
  color: $primary-dark;
  border: none !important;
}
.collection-header-tab-viewer-added-indicator {
  background-color: $secondary-light;
}
.collection-header-search {
  flex: 0 0 auto;
  margin-right: 1rem;
}
.collection-header-filler {
  flex: 1 1 auto;
  min-width: 3rem; // Equal to the width of the action buttons container in the top-right, ensures proper scroll view for collection-header on small screens
}

// OPEN ITEMS
.open-item-tabs-scrollbox {
  min-height: calc(2.875rem + 4px); // works for now, but should probably be fixed for different screen and font sizes and stuff
  overflow-x: scroll;
}
.open-item-tabs-scrollbox::-webkit-scrollbar-thumb:hover {
  background-color: rgba($primary-dark, 0.75);
}
.open-item-tabs {
  position: sticky;
  top: 0;
  height: calc(2rem + 1px); // (font-size * 1.5 line-height) + 0.5 padding + 1px for border
  background: #fff;
  z-index: 5;
  white-space: nowrap;
  user-select: none;
  visibility: visible;
}
.open-item-collection-tab {
  width: 10rem !important;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis !important;
}
.open-item-tab {
  position: relative;
  display: inline-block;
  width: 8rem;
  color: rgba(74, 74, 74, 0.7);
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: clip;
  border-bottom: 1px solid $primary;
  border-left: 1px solid transparent;
  border-right: 1px solid transparent;
  padding: 0.25rem 0;
  background: #ffffff;
}
.open-item-tabs span {
  display: inline-block;
  max-width: 100%;
}
.open-item-tab:first-child {
  padding-left: 0.5rem;
  border-left: 1px solid transparent !important;
  position: sticky;
  left: 0;
  background: $primary-light;
  z-index: 1;
}
.icon-close {
  position: absolute;
  top: 0.35rem;
  right: 0.25rem;
  border-radius: 50%;
  opacity: 0.4;
  font-size: 0.9rem;
  transition: opacity 0.2s;
}
.open-item-tab:hover > .icon-close {
  opacity: 0.6;
}
.icon-close:hover,
.icon-pin:hover {
  opacity: 0.9 !important;
}
.icon-pin {
  position: absolute;
  top: 0.25rem;
  left: 0.25rem;
  border-radius: 50%;
  opacity: 1;
  background: inherit;
  top: 0rem;
  left: 0rem;
  font-size: 0.6rem;
  opacity: 0.4;
  transition: all 0.15s;
  transition-delay: 0.1s;
}
.open-item-tab:hover > .icon-pin {
  font-size: 0.85rem;
  opacity: 0.7;
}
.hide-pin-icon {
  opacity: 0;
}
.open-item-tab-active {
  color: rgba(74, 74, 74, 1);
  border-bottom: 1px solid transparent;
  border-left: 1px solid $primary;
  border-right: 1px solid $primary;
  background: #efefef !important;
  transition: all 0.2s;
}
.open-item-tab:hover {
  opacity: 1;
  transition: all 0.2s;
}
.open-item-tab-text {
  width: 6.5rem;
  font-size: 1rem;
  overflow-x: hidden;
  text-overflow: clip;
  padding-left: 0.5rem;
}
.open-item-tabs-filler {
  position: relative;
  display: inline-block;
  border-bottom: 1px solid $primary;
}

// COLLECTION CONTENT
.collection-content {
  position: relative;
  width: 100%;
  height: 100%;
  text-align: left;
  overflow: hidden;
}
</style>
